import { IconProps } from "@velaro/velaro-component-lib";
import React from "react";
import { Link } from "react-router-dom";

export function Card({
  to,
  title,
  subtitle,
  icon,
  status,
  variant = "default",
  isFirstCard = false,
  onClick,
  appStatus
}: {
  to: string;
  title: string;
  subtitle: string;
  icon?: string | React.FC<IconProps>;
  status?: string;
  variant?: "default" | "integrations";
  isFirstCard?: boolean;
  onClick?: () => void;
  appStatus?: string;
}) {
  if (variant === "integrations") {
    if (isFirstCard) {
      return (
        <div className="w-full mb-4 cursor-pointer" onClick={onClick}>
          <div className="h-48 md:h-60 p-6 bg-white rounded-xl border-2 border-dashed border-slate-300 w-full hover:border-cornflower-blue-400 hover:bg-cornflower-blue-50 transition-all flex flex-col items-center justify-center gap-3 group">
            <div className="w-12 h-12 rounded-full bg-slate-100 group-hover:bg-cornflower-blue-100 flex items-center justify-center transition-colors">
              {icon && typeof icon !== "string" && React.createElement(icon, { className: "w-5 h-5 text-slate-500 group-hover:text-cornflower-blue-600" })}
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-slate-700 group-hover:text-cornflower-blue-700">{title}</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-[200px]">{subtitle}</p>
            </div>
          </div>
        </div>
      );
    }

    const isConnected = appStatus === "Connected";

    return (
      <div className="w-full mb-4 cursor-pointer" onClick={onClick}>
        <div className="min-h-[200px] bg-white rounded-xl border border-slate-200 w-full hover:border-cornflower-blue-300 hover:shadow-md transition-all flex flex-col p-5 gap-4 group relative overflow-hidden">
          {/* Connected indicator stripe */}
          {isConnected && (
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-green-400 rounded-t-xl" />
          )}

          {/* Header row: logo + status badge */}
          <div className="flex items-start justify-between">
            {icon && (
              <div className="w-14 h-14 rounded-xl border border-slate-100 bg-slate-50 flex items-center justify-center p-2 shadow-sm flex-shrink-0">
                {typeof icon === "string" ? (
                  <img src={icon} alt="" className="w-full h-full object-contain" />
                ) : (
                  React.createElement(icon, { className: "w-7 h-7" })
                )}
              </div>
            )}
            {appStatus && appStatus !== "" && (
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${
                  isConnected
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-slate-100 text-slate-500 border border-slate-200"
                }`}
              >
                {isConnected && (
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                )}
                {appStatus}
              </span>
            )}
          </div>

          {/* Name + subtitle */}
          <div className="flex-1 min-h-0">
            <h3 className="font-semibold text-slate-800 text-sm leading-snug group-hover:text-cornflower-blue-700 transition-colors">{title}</h3>
            <p className="text-xs text-slate-500 mt-1 line-clamp-3 leading-relaxed">{subtitle}</p>
          </div>

          {/* Arrow hint on hover */}
          <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-xs text-cornflower-blue-500 font-medium flex items-center gap-1">
              Configure
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </span>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="w-80 h-36">
        <Link to={to}>
          <div className="border float-left rounded w-80 h-36 mr-4 mb-4 hover:shadow group">
            <div className="font-semibold mb-2 bg-cornflower-blue-400 group-hover:bg-cornflower-blue-500 text-slate-50 p-3 group">
              {title}
            </div>
            <div className="text-sm p-3">{subtitle}</div>
          </div>
        </Link>
      </div>
    );
  }
}
